import { useEffect, useState } from "react";
import {
  TikTokCreatorInfo,
  TikTokNotConnectedError,
  TikTokReconnectRequiredError,
  getTikTokCreatorInfo,
} from "../services/tiktokAuthentication";

export type TikTokCreatorStatus =
  | "idle"
  | "loading"
  | "ready"
  | "reconnect"
  | "not_connected"
  | "error";

interface Params {
  customerId?: string;
  profileId?: string;
  idToken?: string;
  enabled: boolean;
}

const CREATOR_INFO_TTL_MS = 60_000;
// Errors (reconnect / not connected / 5xx) are retried soon, not remembered for a minute.
const CREATOR_INFO_ERROR_TTL_MS = 5_000;

interface CacheEntry {
  at: number;
  ttl: number;
  promise: Promise<TikTokCreatorInfo>;
}
const creatorInfoCache = new Map<string, CacheEntry>();

/** Test hook: forget every cached creator_info. */
export function resetTikTokCreatorInfoCache(): void {
  creatorInfoCache.clear();
}

/**
 * creator_info is rate-limited by TikTok (~20/min/user) and every TikTok card on a
 * page asks for it, so share one request per customer:profile: concurrent callers
 * reuse the in-flight promise and later ones reuse the result for ~60s.
 */
function getCreatorInfoShared(
  customerId: string,
  profileId: string,
  idToken: string
): Promise<TikTokCreatorInfo> {
  const key = `${customerId}:${profileId}`;
  const hit = creatorInfoCache.get(key);
  if (hit && Date.now() - hit.at < hit.ttl) return hit.promise;
  const entry: CacheEntry = {
    at: Date.now(),
    ttl: CREATOR_INFO_TTL_MS,
    promise: getTikTokCreatorInfo(customerId, profileId, idToken),
  };
  creatorInfoCache.set(key, entry);
  entry.promise.catch(() => {
    entry.at = Date.now();
    entry.ttl = CREATOR_INFO_ERROR_TTL_MS;
  });
  return entry.promise;
}

/** Live creator_info (nickname, privacy options, limits) for the composer. */
export function useTikTokCreatorInfo({ customerId, profileId, idToken, enabled }: Params) {
  const [creatorInfo, setCreatorInfo] = useState<TikTokCreatorInfo | null>(null);
  const [status, setStatus] = useState<TikTokCreatorStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCreatorInfo(null);
    setError(null);
    if (!enabled) {
      setStatus("idle");
      return;
    }
    if (!profileId) {
      setStatus("not_connected");
      return;
    }
    if (!customerId || !idToken) {
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    getCreatorInfoShared(customerId, profileId, idToken)
      .then((info) => {
        if (cancelled) return;
        setCreatorInfo(info);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof TikTokReconnectRequiredError) setStatus("reconnect");
        else if (err instanceof TikTokNotConnectedError) setStatus("not_connected");
        else {
          setStatus("error");
          setError(err instanceof Error ? err.message : null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, profileId, idToken, enabled]);

  return { creatorInfo, status, error };
}
